import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useActiveWorkspace } from '@/hooks/use-active-workspace';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';
import { useIsAgency } from '@/hooks/use-billing';
import { useTheme } from '@/hooks/use-theme';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { BrandIdentity } from '@/components/branding/BrandIdentity';
import {
  IconLogout,
  IconShield,
  IconSun,
  IconMoon,
  IconChevronLeft,
  IconChevronRight,
  IconClose
} from './sidebar-icons';
import { isSectionOpen, visibleNavSections, type SectionToggle } from './sidebar-nav';
import styles from './Sidebar.module.scss';

const COLLAPSED_KEY = 'zaptor:sidebar-collapsed';

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function profileInitials(name: string | null | undefined, email: string | null | undefined): string {
  const source = (name?.trim() || email?.trim() || 'U').trim();
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

interface SidebarProps {
  mobileOpen?: boolean;
  onRequestClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onRequestClose }: SidebarProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { me, activeRole, isOwner } = useActiveWorkspace();
  const { isAdmin } = usePlatformAdmin();
  const { isAgency } = useIsAgency();
  const { theme, toggle } = useTheme();
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [sectionToggles, setSectionToggles] = useState<Record<string, SectionToggle>>({});
  const themeToggleLabel = theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro';
  const ThemeIcon = theme === 'dark' ? IconSun : IconMoon;
  const sections = visibleNavSections({ role: activeRole, isOwner, isAgency });

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [collapsed]);

  async function handleLogout() {
    await signOut();
    navigate('/login', { replace: true });
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? `${styles.link} ${styles.active}` : styles.link;

  return (
    <aside
      id="app-navigation"
      className={`${styles.root} ${collapsed ? styles.collapsed : ''} ${mobileOpen ? styles.mobileOpen : ''}`}
      aria-label="Navegação principal"
    >
      <div className={styles.brand}>
        <BrandIdentity compact={collapsed} className={styles.brandIdentity} />
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? 'Expandir menu' : 'Minimizar menu'}
          title={collapsed ? 'Expandir menu' : 'Minimizar menu'}
        >
          {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
        </button>
        <button
          type="button"
          className={styles.closeMobileBtn}
          onClick={onRequestClose}
          aria-label="Fechar navegação"
        >
          <IconClose />
        </button>
      </div>
      {!collapsed && <WorkspaceSwitcher />}
      <div className={styles.navScroll}>
      <nav className={styles.nav}>
        {sections.map((section) => {
          const sectionOpen = isSectionOpen(section, location, sectionToggles[section.id]);
          const SectionIcon = section.collapsible?.Icon;
          return (
            <div key={section.id} className={styles.section}>
              {SectionIcon ? (
                <button
                  type="button"
                  className={`${styles.sectionToggle} ${sectionOpen ? styles.sectionToggleOpen : ''}`}
                  onClick={() => setSectionToggles((current) => ({
                    ...current,
                    [section.id]: { open: !sectionOpen, at: location.key }
                  }))}
                  aria-expanded={sectionOpen}
                  title={collapsed ? section.label : undefined}
                >
                  <span className={styles.linkIcon}><SectionIcon /></span>
                  {!collapsed && <span>{section.label}</span>}
                  {!collapsed && <IconChevronRight />}
                </button>
              ) : (
                section.label && !collapsed && <h6 className={styles.sectionLabel}>{section.label}</h6>
              )}
              {sectionOpen && section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={linkClass}
                  title={collapsed ? item.label : undefined}
                  onClick={onRequestClose}
                >
                  <span className={styles.linkIcon}><item.Icon /></span>
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      </div>
      <div className={styles.footer}>
        <div className={styles.accountCard}>
          <NavLink
            to="/profile"
            className={({ isActive }) => isActive ? `${styles.profileLink} ${styles.profileActive}` : styles.profileLink}
            title={collapsed ? 'Perfil' : undefined}
            aria-label="Perfil"
            onClick={onRequestClose}
          >
            <span className={styles.profileAvatar}>
              {me?.avatar_url ? (
                <img src={me.avatar_url} alt="" />
              ) : (
                <span>{profileInitials(me?.full_name, me?.email)}</span>
              )}
            </span>
            {!collapsed && (
              <span className={styles.profileInfo}>
                <span className={styles.profileName}>{me?.full_name || 'Perfil'}</span>
                <span className={styles.profileEmail}>{me?.email}</span>
                {isAdmin && (
                  <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', color: 'var(--zaptor-accent)', textTransform: 'uppercase' }}>
                    Superadmin
                  </span>
                )}
              </span>
            )}
          </NavLink>
          {isAdmin && (
            <NavLink
              to="/admin"
              className={styles.themeToggle}
              title={collapsed ? 'Administração' : undefined}
              aria-label="Administração"
            >
              <IconShield />
              {!collapsed && <span>Administração</span>}
            </NavLink>
          )}
          <button
            type="button"
            className={styles.themeToggle}
            onClick={toggle}
            title={collapsed ? themeToggleLabel : undefined}
            aria-label={themeToggleLabel}
          >
            <ThemeIcon />
            {!collapsed && <span>{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>}
          </button>
          <button
            className={styles.logout}
            onClick={handleLogout}
            title={collapsed ? 'Sair' : undefined}
            aria-label="Sair"
          >
            <IconLogout />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>
      </div>
    </aside>
  );
}
